import { useMemo } from 'react';

import useMaterieelTypen from '@/state/selectors/useMaterieelTypen';

import GroupedCreatableSelectField from './GroupedCreatableSelectField';

type MaterieelTypeOption = { label: string; value: string };
type MaterieelTypeGroupedOption = {
  label: string;
  options: { label: string; value: string[] }[];
};

type MaterieelTypeSelectFieldProps = {
  control: any;
  name: string;
  label?: string;
  testId?: string;
  placeholder?: string;
  defaultValue?: MaterieelTypeOption[];
  marginBottom?: number;
};

// Predefined category definitions: [categoryLabel, memberTypes (first item = "Alle X toevoegen" label)]
const categories: [string, string[]][] = [
  [
    'Sprinters',
    [
      'Alle sprinters toevoegen',
      'SNG-3',
      'SNG-4',
      'SNG-3T',
      'SNG-4T',
      'SLT-4',
      'SLT-6',
      'SLT-6S',
      'FLIRT TAG-2',
      'FLIRT FFF-3',
      'FLIRT FFF-4',
    ],
  ],
  [
    'Intercities',
    [
      'Alle intercities toevoegen',
      'ICM-3',
      'ICM-4',
      'VIRM-4',
      'VIRM-6',
      'DDZ-4',
      'DDZ-6',
      'ICNG-5',
      'ICNG-8',
      'ICNG-8B',
    ],
  ],
  ['ONB Sprinter 1', ['Alle ONB Sprinter 1 toevoegen', 'SNG-3', 'SNG-4', 'SNG-3T', 'SNG-4T', 'FLIRT TAG-2']],
  ['ONB Sprinter 2', ['Alle ONB Sprinter 2 toevoegen', 'SLT-4', 'SLT-6', 'SLT-6S', 'FLIRT FFF-3', 'FLIRT FFF-4']],
  [
    'ONB Intercity 1',
    ['Alle ONB Intercity 1 toevoegen', 'ICM-3', 'ICM-4', 'DDZ-4', 'DDZ-6', 'ICNG-5', 'ICNG-8', 'ICNG-8B'],
  ],
  ['ONB Intercity 2', ['Alle ONB Intercity 2 toevoegen', 'VIRM-4', 'VIRM-6']],
  ['PC Brussel', ['Alle PC Brussel toevoegen', 'SWB6-25KV-2+6', 'ICNG-8B']],
  [
    'PC Duitsland',
    [
      'Alle PC Duitsland toevoegen',
      'NEO-ICE3',
      'E-LOC-VECT',
      'DB-BER9-9',
      'DB-B5-5',
      'DB-B6-6',
      'DB-B7-7',
      'DB-B8-8',
      'ICNG-8D',
    ],
  ],
  ['PC Eurostar', ['Alle PC Eurostar toevoegen', 'TGV-PBA', 'EUR-PBA']],
  [
    'INT Volledig',
    [
      'Alle INT volledig toevoegen',
      'TGV-PBA',
      'EUR-PBA',
      'NEO-ICE3',
      'E-LOC-VECT',
      'DB-BER9-9',
      'DB-B5-5',
      'DB-B6-6',
      'DB-B7-7',
      'DB-B8-8',
      'SWB6-25KV-2+6',
      'ICNG-8B',
    ],
  ],
];

// Hardcoded base-type groups: [prefix, label, hardcodedMembers]
// Order matters: more specific prefixes must come before less specific ones
// (e.g. ICNGB before ICNG) so types are assigned to the most specific group.
// hardcodedMembers are always included even if not present in the runtime API data.
const baseTypeGroups: [string, string, string[]][] = [
  ['SLT', 'SLT', ['SLT-4', 'SLT-6', 'SLT-6S']],
  ['SNG', 'SNG', ['SNG-3', 'SNG-4', 'SNG-3T', 'SNG-4T']],
  ['ICM', 'ICM', ['ICM-3', 'ICM-4']],
  ['VIRM', 'VIRM', ['VIRM-4', 'VIRM-6']],
  ['DDZ', 'DDZ', ['DDZ-4', 'DDZ-6']],
  ['ICNG', 'ICNG', ['ICNG-5', 'ICNG-8', 'ICNG-8B', 'ICNG-8D']],
  ['FLIRT', 'FLIRT', ['FLIRT FFF-3', 'FLIRT FFF-4', 'FLIRT TAG-2']],
  ['DB', 'DB', ['DB-B5-5', 'DB-B6-6', 'DB-B7-7', 'DB-B8-8', 'DB-BER9-9']],
  ['E-LOC', 'E-LOC', ['E-LOC-1700', 'E-LOC-TR25', 'E-LOC-TRBE', 'E-LOC-VECT']],
  ['SW', 'SW', []],
];

export function buildGroupedOptions(allTypes: string[]): MaterieelTypeGroupedOption[] {
  const groups: MaterieelTypeGroupedOption[] = [];
  const assignedToBaseGroup = new Set<string>();

  // 1. Base-type groups first — merge hardcoded members with runtime matches
  for (const [prefix, label, hardcoded] of baseTypeGroups) {
    const runtimeMatches = allTypes.filter((t) => t.startsWith(prefix) && !assignedToBaseGroup.has(t));
    const members = [...new Set([...hardcoded, ...runtimeMatches])].sort((a, b) => a.localeCompare(b));
    if (members.length >= 1) {
      members.forEach((t) => assignedToBaseGroup.add(t));
      groups.push({
        label,
        options: [
          ...members.map((t) => ({ label: t, value: [t] })),
          { label: `Alle ${label}'s toevoegen`, value: members },
        ],
      });
    }
  }

  // 2. Predefined category groups
  for (const [label, members] of categories) {
    const allLabel = members[0];
    const types = members.slice(1);
    groups.push({
      label,
      options: [{ label: allLabel, value: types }, ...types.map((t) => ({ label: t, value: [t] }))],
    });
  }

  // 3. "Overige" for types not in any group
  const allCategoryTypes = new Set(categories.flatMap(([, members]) => members.slice(1)));
  const overige = allTypes.filter((t) => !allCategoryTypes.has(t) && !assignedToBaseGroup.has(t));

  if (overige.length > 0) {
    groups.push({
      label: 'Overige',
      options: overige.map((t) => ({ label: t, value: [t] })),
    });
  }

  return groups;
}

export default function MaterieelTypeSelectField({
  control,
  name,
  label = 'Materieeltype',
  testId = 'materieelTypen',
  placeholder = 'Voeg materieeltype(n) toe',
  defaultValue = [],
  marginBottom = 0,
}: MaterieelTypeSelectFieldProps) {
  const lijstVanMatTypen = useMaterieelTypen();

  const combinedOptions = useMemo(() => buildGroupedOptions(lijstVanMatTypen), [lijstVanMatTypen]);

  return (
    <GroupedCreatableSelectField
      control={control}
      name={name}
      label={label}
      testId={testId}
      placeholder={placeholder}
      defaultValue={defaultValue}
      options={combinedOptions}
      filterMatchFrom='any'
      formatCreateLabel={(input) => `MaterieelType: ${input}`}
      normalizeCreatedValue={(val) => val.trim().toUpperCase()}
      marginBottom={marginBottom}
    />
  );
}
